/**
 *
 * Datepicker component
 *
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { Variants } from "@dhl-official/stencil-library";
import dayjs from "dayjs";
import locals from "dayjs/locale.json";
import { keys } from "root/libs/constants/src";
import useOutsideClick from "./shared/useOutsideClick";
import { changeMonthBy, changeYearBy, parseToDayJs } from "./utils/dateUtils";
import { shouldUseNativeDatePicker } from "./utils/deviceUtils";
import { DATE_FORMAT, WEEK } from "./shared/constants";
// eslint-disable-next-line import/no-cycle
import { Calendar } from "./shared/Calendar";
import { CalendarWrapper } from "./shared/CalendarWrapper";
import { FullDateSrOnly } from "./shared/FullDateSrOnly";
import {
  Container,
  DateToggle,
  StyledIcon,
  DatePickerInputField,
} from "./shared/styled";
import { useDate } from "./shared/useDate";

const Datepicker = ({
  ariaLabelForInput,
  ariaLabelForDatePicker,
  ariaLabelForNextMonth,
  ariaLabelForNextYear,
  ariaLabelForPrevMonth,
  ariaLabelForPrevYear,
  className,
  dataTestid,
  date: dt,
  displayFormat,
  firstDayOfTheWeek,
  id,
  locale,
  maxDate: maxDt,
  minDate: minDt,
  name,
  onChange,
  validation,
  variant,
}) => {
  // Use the locale, if it is in dayjs. Otherwise fall back to english
  const safeLocale = locals.map(({ key }) => key).includes(locale)
    ? locale
    : "en";

  // to control the state of the cal component
  const [calDate, setCalDate] = useState(dayjs(new Date()));
  const selected = useDate(dt, safeLocale, displayFormat);

  // state of the calender to show or hide
  const [isOpen, setIsOpen] = useState(false);
  // to control the animations
  const datePickerRef = useRef(null);

  // min and max dates could be strings, should handle them
  const minDate = minDt
    ? parseToDayJs(minDt, displayFormat, safeLocale)
    : minDt;
  const maxDate = maxDt
    ? parseToDayJs(maxDt, displayFormat, safeLocale)
    : maxDt;

  // ------------------------
  // Helpers
  // ------------------------
  const openCalendar = () => setIsOpen(true);
  const closeCalendar = () => setIsOpen(false);
  const onChangeCallbackHandler = () => {
    if (!selected.isSame(dt)) {
      onChange(selected.date);
    }
    closeCalendar();
  };
  const toggleCalendar = () => {
    if (isOpen) {
      onChangeCallbackHandler();
    }
    setIsOpen(!isOpen);
  };

  // ------------------------
  // Listeners
  // ------------------------

  // Whenever there is a change in selected date
  useEffect(() => {
    if (selected.date.isValid()) {
      setCalDate(selected.date);
      onChangeCallbackHandler();
    }
  }, [selected.date]);

  // ------------------------
  // Event handlers
  // ------------------------

  /**
   * When user clicks on next or prev button
   * @param {number} nav
   */
  const onChangeMonthHandler = (nav) => {
    setCalDate(changeMonthBy(calDate, nav, 1));
  };

  const onChangeYearHandler = (nav) => {
    setCalDate(changeYearBy(calDate, nav, 1));
  };

  /**
   * Handles only when user selects a date
   * from the calendar component
   * @param {dayjs} dayjsDate
   */
  const onDateSelectHandler = (dayjsDate) => {
    selected.setDate(dayjsDate);
  };

  /**
   * Handles only when user enters the value
   * in the Input field keep as it is
   * @param {SyntheticEvent} event
   */
  const onInputChangeHandler = (event) => {
    const newVal = event.target.value;
    // On change in input just set the value
    // debounce will take care of setting the
    selected.setValue(newVal);
    if (!parseToDayJs(newVal, DATE_FORMAT, safeLocale).isValid()) {
      openCalendar();
    }
  };

  // When user clicks outside of the calendar
  useOutsideClick(datePickerRef, () => {
    if (isOpen) {
      onChangeCallbackHandler();
    }
  });

  // When user press Enter key in the input field
  const onKeyDownHandler = (event) => {
    if (event.key === keys.ESC) {
      onChangeCallbackHandler();
    } else if (event.key === keys.RETURN) {
      toggleCalendar();
    }
  };

  return (
    <Container
      id={`${id}-container`}
      dataTestid={dataTestid}
      ref={datePickerRef}
      className={className}
    >
      <FullDateSrOnly id="dhl-input-description" msg={selected.getFullDate()} />
      <DatePickerInputField
        isOpen={isOpen}
        aria-describedby="dhl-input-description"
        dataAriaLabel={ariaLabelForInput}
        dataAriaHasPopup="true"
        aria-controls={isOpen ? `${id}-calendar-wrapper` : undefined}
        id={id}
        name={name}
        value={selected.value}
        variant={{
          type: variant.type,
          placeholder: DATE_FORMAT,
          label: variant.label,
        }}
        validation={validation}
        dataTestid={`${dataTestid}-input`}
        inputEvent={onInputChangeHandler}
        focusEvent={openCalendar}
        keyDownEvent={onKeyDownHandler}
        type={shouldUseNativeDatePicker ? "date" : "text"}
        autocomplete="off"
        showClearButton={false}
      />
      {validation.type !== Variants.valid && (
        <DateToggle onClick={toggleCalendar}>
          <StyledIcon />
        </DateToggle>
      )}
      <CalendarWrapper
        isOpen={isOpen}
        ariaLabel={ariaLabelForDatePicker}
        id={`${id}-calendar-wrapper`}
      >
        <Calendar
          ariaLabelForNextMonth={ariaLabelForNextMonth}
          ariaLabelForNextYear={ariaLabelForNextYear}
          ariaLabelForPrevMonth={ariaLabelForPrevMonth}
          ariaLabelForPrevYear={ariaLabelForPrevYear}
          calDate={calDate}
          id={`${id}-calendar`}
          dataTestid={`${dataTestid}-calendar`}
          dateFormat={DATE_FORMAT}
          firstDayOfTheWeek={firstDayOfTheWeek}
          isOpen={isOpen}
          locale={safeLocale}
          maxDate={maxDate}
          minDate={minDate}
          onChangeMonth={onChangeMonthHandler}
          onChangeYear={onChangeYearHandler}
          onSelect={onDateSelectHandler}
          selectedDate={selected.date}
        />
      </CalendarWrapper>
    </Container>
  );
};

Datepicker.defaultProps = {
  id: "pl-dp",
  dataTestid: "pl-dp",
  variant: {
    label: "Date",
    type: Variants.static,
  },
  displayFormat: "DD MMM YYYY",
  onChange: () => {},
  date: dayjs(""),
  locale: "en",
};

Datepicker.propTypes = {
  /** Id to the root element */
  id: PropTypes.string,
  /** Class to the root element */
  className: PropTypes.string,
  /** The test id attached to the component as a data-testid attribute. */
  dataTestid: PropTypes.string,
  /** Which day would you like to start the week? based on WEEK Prop */
  firstDayOfTheWeek: PropTypes.oneOf(Object.values(WEEK)),
  /** variant PropTypes of InputField */
  // TODO: Update when the DUIL supports named exports on variants
  variant: PropTypes.any,
  /** validation PropTypes of InputField */
  // TODO: Update when the DUIL supports named exports on validations
  validation: PropTypes.any,
  /** Locale of the date picker */
  locale: PropTypes.string,
  /** Date Format of the text after user picks a date */
  displayFormat: PropTypes.string,
  /** What is the selected date you want to highlight */
  date: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(dayjs),
    PropTypes.string,
  ]),
  /** on selecting a date from calendar. You always get a valid date in this case
   * returns ({string|dayjs} date, boolean isValid)
   */
  onChange: PropTypes.func,
  /**
   *
   * The ariaLabel property is used to define a string that labels the current element.
   * Use it in cases where a text label is not visible on the screen.
   * If there is visible text labeling the element, use aria-labelledby instead.
   *
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-label_attribute
   */
  ariaLabelForDatePicker: PropTypes.string,
  ariaLabelForInput: PropTypes.string,
  ariaLabelForNextMonth: PropTypes.string,
  ariaLabelForNextYear: PropTypes.string,
  ariaLabelForPrevMonth: PropTypes.string,
  ariaLabelForPrevYear: PropTypes.string,
  /** Name of the form input */
  name: PropTypes.string,
  /** use this prop to disable the dates before minDate */
  minDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(dayjs),
    PropTypes.string,
  ]),
  /** use this prop to disable the dates after maxDate */
  maxDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(dayjs),
    PropTypes.string,
  ]),
};

export default injectIntl(Datepicker);
